/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useMemo } from 'react';
import { Badge as BadgeType, Icons } from '@wit/vtv-x-foundation-lib';

export enum BadgeClassName {
  AIRS_ON = 'airsOn',
  EXPIRES = 'expires',
  CATCHUP = 'catchup',
  RESTART = 'restart',
  RECORDING = 'recording',
  RECORDABLE = 'recordable',
  SERIES = 'series',
  RECORDING_PROTECTED = 'recordingProtected',
  SERIES_PROTECTED = 'seriesProtected',
  WISHLIST = 'wishlist',
  PARENTAL_LOCKED = 'parentalLocked',
  PARENTAL_UNLOCKED = 'parentalUnlocked',
  EURO = 'euro',
  DOLLAR = 'dollar',
  POUND = 'pound',
  NONE = '',
}

function badgeIconToClassName(icon: Icons) {
  switch (icon) {
    case Icons.WISHLIST:
      return BadgeClassName.WISHLIST;
    case Icons.RECORDINGS:
      return BadgeClassName.RECORDABLE;
    case Icons.LOCK:
      return BadgeClassName.PARENTAL_LOCKED;
    case Icons.LOCK_OPEN:
      return BadgeClassName.PARENTAL_UNLOCKED;
    case Icons.RECORDING_SINGLE:
    case Icons.RECORDING_SINGLE_SCHEDULED:
      return BadgeClassName.RECORDING;
    case Icons.RECORDING_SERIES:
    case Icons.RECORDING_SERIES_SCHEDULED:
      return BadgeClassName.SERIES;
    case Icons.RESTART:
      return BadgeClassName.RESTART;
    case Icons.CLOCK:
      return BadgeClassName.EXPIRES;
    case Icons.PROTECTED_RECORDING:
      return BadgeClassName.RECORDING_PROTECTED;
    case Icons.PLAY_CIRCLE:
      return BadgeClassName.CATCHUP;
    case Icons.DOLLAR:
    case Icons.POUND:
      return BadgeClassName.DOLLAR;
    case Icons.EURO:
      return BadgeClassName.EURO;
    case Icons.AIRS_ON:
      return BadgeClassName.AIRS_ON;
    default:
      return BadgeClassName.NONE;
  }
}

interface Props {
  badge: BadgeType;
  bemBlock: string;
  bemModifierPrefix?: string;
}

export default function Badge({ badge, bemBlock, bemModifierPrefix }: Props): JSX.Element | null {
  const className = useMemo(() => {
    return badgeIconToClassName(badge.icon);
  }, [badge]);

  const id = useMemo(() => {
    const bemModifier = bemModifierPrefix ? `${bemModifierPrefix}-${className.toLowerCase()}` : className.toLowerCase();
    return `${bemBlock}_badge___${bemModifier}`;
  }, [bemBlock, bemModifierPrefix, className]);

  return className !== BadgeClassName.NONE ? (
    <div className={`badge ${className} ${badge.color ? 'active' : ''}`} id={id} />
  ) : (
    <></>
  );
}
